/**
 * Admin Storage Abstraction Layer - Public API
 *
 * Sprint B (Master Plan v5.1): all keys are tenant-scoped by default.
 * Use { tenantScope: 'global' } only for genuinely cross-tenant data
 * (asset chunk reload guard, analytics device fingerprint).
 *
 * Window provides __TENANT_SLUG__ via TenantContext after admin mounts;
 * any storage write before bootstrap throws (rule #2 — no t:unknown:*).
 */

import { StorageSlot } from './StorageSlot'
import { STORAGE_KEYS, SESSION_KEYS } from './constants'

export { StorageSlot } from './StorageSlot'
export {
  runPostBootstrapMigrations,
  migrateAdminAuthTokenForCurrentTenant,
  clearAllAdminStorage,
} from './migrations'
export { STORAGE_KEYS, SESSION_KEYS, isBrowser } from './constants'

// ============================================================================
// Storage Registry — all tenant-scoped except where noted.
// ============================================================================

export const storage = {
  // -------------------------------------------------------------------------
  // Authentication — TENANT-SCOPED (auth_token belongs to one tenant per user)
  // -------------------------------------------------------------------------

  authToken: new StorageSlot<string>(STORAGE_KEYS.AUTH_TOKEN, {
    storage: 'local',
    serialize: (token) => token,
    deserialize: (raw) => raw,
  }),

  // -------------------------------------------------------------------------
  // Form drafts — TENANT-SCOPED (bouquet draft belongs to current tenant)
  // -------------------------------------------------------------------------

  bouquetDraft: new StorageSlot<unknown>(STORAGE_KEYS.BOUQUET_DRAFT, {
    storage: 'local',
  }),

  bouquetCompositionDraft: new StorageSlot<unknown>(
    STORAGE_KEYS.BOUQUET_COMPOSITION_DRAFT,
    { storage: 'local' },
  ),

  // -------------------------------------------------------------------------
  // UI state — TENANT-SCOPED (different tenants may have different layouts)
  // -------------------------------------------------------------------------

  groupedNav: new StorageSlot<boolean>(STORAGE_KEYS.GROUPED_NAV, {
    storage: 'local',
    serialize: (v) => String(v),
    deserialize: (raw) => raw === 'true',
  }),

  expandedGroups: new StorageSlot<Record<string, boolean>>(
    STORAGE_KEYS.EXPANDED_GROUPS,
    { storage: 'local' },
  ),

  accordionState: new StorageSlot<Record<string, boolean>>(
    STORAGE_KEYS.ACCORDION_STATE,
    { storage: 'local' },
  ),

  // -------------------------------------------------------------------------
  // Settings drafts — TENANT-SCOPED
  // -------------------------------------------------------------------------

  safeSettingsDraft: new StorageSlot<unknown>(STORAGE_KEYS.SAFE_SETTINGS_DRAFT, {
    storage: 'local',
  }),

  // -------------------------------------------------------------------------
  // Analytics — GLOBAL by exception (device fingerprint is browser-level)
  // -------------------------------------------------------------------------
  // NOTE per Master Plan v5.1 review: even fingerprint can leak attribution
  // across tenants. Keep tenant-scoped for stronger isolation.
  analyticsDevice: new StorageSlot<{
    deviceId: string
    sessionId: string
  }>(STORAGE_KEYS.ANALYTICS_DEVICE, {
    storage: 'local',
  }),

  // -------------------------------------------------------------------------
  // Session storage — chunk reload guard is GLOBAL (asset-level recovery)
  // -------------------------------------------------------------------------

  chunkReload: new StorageSlot<string>(SESSION_KEYS.CHUNK_RELOAD, {
    storage: 'session',
    tenantScope: 'global',
    serialize: (v) => v,
    deserialize: (raw) => raw,
  }),
} as const

export type StorageRegistry = typeof storage

/**
 * Helper for cases where the storage key is computed at runtime (e.g. per-form
 * draft keyed by entity id, or analytics events keyed by date). Creates a fresh
 * StorageSlot under the hood — same tenant-prefixing rules apply.
 *
 * For static keys, prefer adding to the `storage` registry above.
 */
export function dynamicSlot<T = unknown>(
  key: string,
  options?: { storage?: 'local' | 'session'; ttl?: number; tenantScope?: 'tenant' | 'global' },
): StorageSlot<T> {
  return new StorageSlot<T>(key, options)
}
